import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, RefreshCw, Loader2, TrendingUp, ThumbsDown, MessageSquare, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Star, label: 'NPS Analytics', active: true },
];

export default function AdminNpsAnalytics() {
  const { getAuthHeader } = useAuth();
  const [trend, setTrend] = useState(null);
  const [byClin, setByClin] = useState(null);
  const [themes, setThemes] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/admin/nps/trend`, getAuthHeader()),
        axios.get(`${API}/admin/nps/by-clinician`, getAuthHeader()),
        axios.get(`${API}/admin/nps/themes`, getAuthHeader()),
      ]);
      setTrend(a.data); setByClin(b.data); setThemes(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load NPS analytics');
    } finally { setLoading(false); }
  }, [getAuthHeader]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Patient NPS / Satisfaction Trend"
      subtitle="12-week NPS rollup. Identify what's driving promoters vs detractors so you can double-down or fix."
      testId="admin-nps-analytics-page"
    >
      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}
      {!loading && trend && (
        <>
          <div className="cn-card p-4 mb-6 flex items-center gap-3">
            <Button onClick={load} className="cn-btn cn-btn-ghost !py-2" data-testid="nps-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
            <span className="text-xs text-cn-text-muted ml-auto">Generated {new Date(trend.generated_at).toLocaleString()}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="nps-summary">
            <Tile label="Overall NPS" value={trend.overall_nps} icon={Star} tone={trend.overall_nps >= 30 ? 'emerald' : trend.overall_nps < 0 ? 'red' : 'amber'} testid="nps-summary-overall" />
            <Tile label="Total responses" value={trend.total_responses} icon={MessageSquare} testid="nps-summary-responses" />
            <Tile label="Weeks tracked" value={(trend.weekly || []).length} icon={TrendingUp} testid="nps-summary-weeks" />
          </div>

          <div className="cn-card overflow-hidden mb-6" data-testid="nps-weekly-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base">Weekly NPS</h4>
            </div>
            {(trend.weekly || []).length === 0 ? (
              <div className="p-10 text-center text-sm text-cn-text-muted" data-testid="nps-weekly-empty">
                No survey responses in the last 12 weeks.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="nps-weekly-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Week</th>
                      <th className="px-3 py-3 text-right">Responses</th>
                      <th className="px-3 py-3 text-right">NPS</th>
                      <th className="px-3 py-3 text-right">4-wk rolling</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {(trend.weekly || []).map((w, i) => (
                      <tr key={w.week} data-testid={`nps-week-${w.week}`}>
                        <td className="px-5 py-2.5 text-xs">{w.week}</td>
                        <td className="px-3 py-2.5 text-right text-xs">{w.responses}</td>
                        <td className={`px-3 py-2.5 text-right text-xs font-semibold ${w.nps >= 30 ? 'text-emerald-700' : w.nps < 0 ? 'text-red-700' : 'text-amber-700'}`}>{w.nps}</td>
                        <td className="px-3 py-2.5 text-right text-xs">{(trend.rolling_4wk || [])[i]?.rolling_nps ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {byClin && (
            <div className="cn-card p-5 mb-6" data-testid="nps-leaders-card">
              <h4 className="font-display font-semibold text-base mb-3">Leaders & laggards</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-2">Top performers</p>
                  {(byClin.leaders || []).slice(0, 5).map((l) => (
                    <div key={l.clinician_id} className="flex items-center justify-between text-xs py-1 border-b border-cn-border last:border-0" data-testid={`nps-leader-${l.clinician_id}`}>
                      <span>{l.clinician_name}</span>
                      <span className="font-semibold text-emerald-700">{l.nps}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-red-700 mb-2">At-risk</p>
                  {(byClin.laggards || []).slice(0, 5).map((l) => (
                    <div key={l.clinician_id} className="flex items-center justify-between text-xs py-1 border-b border-cn-border last:border-0" data-testid={`nps-laggard-${l.clinician_id}`}>
                      <span>{l.clinician_name}</span>
                      <span className="font-semibold text-red-700">{l.nps}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {themes && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="nps-themes-card">
              <div className="cn-card p-5">
                <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-2 flex items-center gap-1"><Star className="w-3 h-3" /> Promoter themes</p>
                {(themes.promoter_themes || []).map((t) => (
                  <div key={t.theme} className="flex items-center justify-between text-xs py-1" data-testid={`nps-promoter-theme-${t.theme}`}>
                    <span className="text-cn-text capitalize">{t.theme.replace('_', ' ')}</span>
                    <span className="font-semibold text-emerald-700">{t.count}</span>
                  </div>
                ))}
                {(themes.promoter_themes || []).length === 0 && <p className="text-xs text-cn-text-muted">No verbatims yet.</p>}
              </div>
              <div className="cn-card p-5">
                <p className="text-[11px] uppercase tracking-wider font-bold text-red-700 mb-2 flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> Detractor themes</p>
                {(themes.detractor_themes || []).map((t) => (
                  <div key={t.theme} className="flex items-center justify-between text-xs py-1" data-testid={`nps-detractor-theme-${t.theme}`}>
                    <span className="text-cn-text capitalize">{t.theme.replace('_', ' ')}</span>
                    <span className="font-semibold text-red-700">{t.count}</span>
                  </div>
                ))}
                {(themes.detractor_themes || []).length === 0 && <p className="text-xs text-cn-text-muted">No verbatims yet.</p>}
              </div>
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}

function Tile({ label, value, icon: Icon, tone = 'neutral', testid }) {
  const cls = { neutral: 'text-cn-text', red: 'text-red-700', amber: 'text-amber-700', emerald: 'text-emerald-700' }[tone];
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${cls}`} />}
      </div>
      <p className={`font-display font-bold text-3xl ${cls}`}>{value ?? 0}</p>
    </div>
  );
}
